import type {
  DocumentData,
  FirestoreError,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { deleteDoc, setDoc } from 'firebase/firestore';
import React from 'react';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import useEventData from '../SiteSearch/useEventData';
import { getCollectionRef, getDocRef } from './getRef';
import useAuth from './useAuth';

export interface FavoriteSubject {
  type: 'product' | 'registration';
  id: string;
}

export type Favorite = FavoriteSubject & { addedAt: Date; id: string };

type UseFavorites = {
  favorites: Favorite[] | undefined;
  isLoading: boolean;
  error: undefined | FirestoreError;
  get: (item: FavoriteSubject | undefined) => Favorite | undefined;
  set: (item: FavoriteSubject | undefined) => void;
  unset: (item: FavoriteSubject | undefined) => void;
};

const FavoritesContext = React.createContext<UseFavorites | undefined>(
  undefined
);

export const FavoritesProvider = ({ children }) => {
  const value = useFavoritesData();
  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

function useFavoritesData(): UseFavorites {
  const data = useEventData();
  const [user] = useAuth();
  const userId = user?.uid;
  const favoritesRef = getCollectionRef(
    data?.collection?.collectionId,
    userId,
    'favorites'
  )?.withConverter(converter);
  const [favorites, isLoading, error] = useCollectionData<Favorite>(
    favoritesRef,
    {
      idField: 'id',
      transform: (favorite: Favorite & { addedAt: { toDate: () => any } }) => ({
        ...favorite,
        addedAt: favorite.addedAt && favorite.addedAt.toDate(),
      }),
    }
  );

  function find(item: FavoriteSubject | undefined) {
    return favorites?.find((favorite) => favorite.id === item?.id);
  }
  return {
    favorites,
    isLoading: favoritesRef ? isLoading : true,
    error,
    get(item) {
      return find(item);
    },
    set(item) {
      const favoriteRef = getDocRef(
        data.collection.collectionId,
        userId,
        'favorites',
        item?.id
      );
      if (!favoriteRef) return;
      const favorite = find(item);
      setDoc(favoriteRef, {
        addedAt: favorite?.addedAt || new Date(),
        type: item?.type,
      });
    },
    unset(item) {
      const favoriteRef = getDocRef(
        data.collection.collectionId,
        userId,
        'favorites',
        item?.id
      );
      if (!favoriteRef) return;
      deleteDoc(favoriteRef);
    },
  };
}

export function useFavorite(item: FavoriteSubject | undefined) {
  const favorites = React.useContext(FavoritesContext);
  if (!favorites)
    throw new Error(
      `useTastingNote() hook was used without a <FavoritesProvider>. Add it to your App.`
    );
  const isSet =
    favorites.isLoading || !item ? null : Boolean(favorites.get(item));
  return {
    isLoading: favorites.isLoading,
    error: favorites.error,
    isSet,
    toggle() {
      if (!item) return;
      if (isSet) return void favorites.unset(item);
      favorites.set(item);
    },
    set() {
      if (item) favorites.set(item);
    },
    unset() {
      if (item) favorites.unset(item);
    },
  };
}

const converter = {
  // This is only needed to get the documents typed.
  toFirestore: (tastingNote: Favorite): DocumentData => tastingNote,
  fromFirestore: (snapshot: QueryDocumentSnapshot<Favorite>, options) =>
    snapshot.data(options),
};

export function useFavorites() {
  const data = React.useContext(FavoritesContext);
  if (!data) {
    throw new Error(
      'useFavorites() was used without a <FavoritesProvider>. Add it to your app.'
    );
  }
  return data;
}
